const PUBLIC_SITE_ORIGIN = "https://thechialliance.com";

function normalizePublicEventSlug(value: string | undefined) {
  const slug = value?.trim().toLowerCase();
  return slug && /^[a-z0-9-]+$/.test(slug) ? slug : undefined;
}

/**
 * Builds a public, non-personal invitation URL for an event attendee to create
 * a member profile. It contains no attendee identifier, email address, or
 * entitlement; the local return path is independently validated after sign-in.
 */
export function getEventMemberConversionPath(eventSlug?: string): string {
  const params = new URLSearchParams({ source: "event-registration" });
  const slug = normalizePublicEventSlug(eventSlug);
  if (slug) params.set("returnTo", `/events/${slug}`);
  return `/create-account?${params.toString()}`;
}

/** Absolute URL for transactional attendee communications. */
export function getEventMemberConversionUrl(eventSlug?: string): string {
  return `${PUBLIC_SITE_ORIGIN}${getEventMemberConversionPath(eventSlug)}`;
}

/**
 * Absolute URL of the public event page, for transactional communications.
 *
 * Returns null for a slug that is not a recognizable public slug, so a message
 * omits the link rather than sending an attendee to a broken address.
 */
export function getEventDetailUrl(eventSlug?: string): string | null {
  const slug = normalizePublicEventSlug(eventSlug);
  return slug ? `${PUBLIC_SITE_ORIGIN}/events/${slug}` : null;
}

/**
 * Absolute sign-in URL, optionally returning the member somewhere specific.
 *
 * Sign-in is passwordless, so this carries no credential and is safe in an email:
 * it lands on the page that emails a one-time code. `returnTo` is validated again
 * server-side after sign-in, so a tampered value cannot redirect off-site.
 */
export function getMemberSignInUrl(returnPath = "/my-events"): string {
  const params = new URLSearchParams({ returnTo: returnPath });
  return `${PUBLIC_SITE_ORIGIN}/login?${params.toString()}`;
}
